import { lazy } from 'react';
import {
  Navigate,
  Outlet,
  RouteObject,
  RouterProvider,
  useLocation,
  useParams,
} from 'react-router-dom';

// eslint-disable-next-line import/no-extraneous-dependencies
import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v6';

// import * as Sentry from '@sentry/react';
import { MasterIndexApiPath } from '@/api';
import { UnitShiftListModal } from '@/components';
import { CareLinkModal } from '@/components/Modals/CareLinkModal/CareLinkModal';
import { UnitShiftListSideModal } from '@/components/Modals/UnitShiftListSideModal/UnitShiftListSideModal';
import NavigatePreserveSearch from '@/components/NavigatePreserveSearch';
// import RouteChangeTracker from '@/components/RouteChangeTracker';
import { AppErrorBoundary } from '@/components/TailwindComponents/components/AppErrorBoundary';
import { GeolocationProvider } from '@/contexts/GeolocationContext';
import { AuthLayout } from '@/layouts/AuthLayout';
import { MainLayout } from '@/layouts/MainLayout';
import { CFSInvolvementHistory } from '@/layouts/TabLayout/MasterNames/components/tables/CFSInvolvementHistory';
import { AgencyType, Role } from '@/models';
import { AdminLayout } from '@/pages/AgencyList/AdminLayout';
import { AuditLogsPage } from '@/pages/AgencyList/AuditLogs';
import CallRecordsList from '@/pages/CallRecords/List';
import { CFSCareLinkChatTab } from '@/pages/CFS/CFSCareLink/CFSCareLinkChatTab';
import CFSMedia from '@/pages/CFS/CFSMedia/CFSMedia';
import CFSTimeline from '@/pages/CFS/CFSTimeline/CFSTimeline';
import {
  loadActiveCFSListRoute,
  loadCFSListRoute,
} from '@/pages/CFS/loadCFSRoutes';
import { EmployeeDetailsPage } from '@/pages/Employees/EmployeeDetailsPage';
import { EmployeeDevices } from '@/pages/Employees/EmployeeDevices';
import FirstResponderDashboardPage from '@/pages/FirstResponder/pages/FirstResponderDashboard/FirstResponderDashboardPage';
import HelpPage from '@/pages/Help/HelpPage';
import AddIncidentCodePage from '@/pages/IncidentCodes/AddIncidentCodePage';
import EditIncidentCodePage from '@/pages/IncidentCodes/EditIncidentCodePage';
import IncidentCodesPage from '@/pages/IncidentCodes/IncidentCodesPage';
import ViewIncidentCodePage from '@/pages/IncidentCodes/ViewIncidentCodePage';
import { BookingDataGrid } from '@/pages/JMS/Managament/components/grids/BookingDataGrid';
import { InmateNotesDataGrid } from '@/pages/JMS/Managament/components/grids/InmateNotesDataGrid';
import BookingHistoryTab from '@/pages/JMS/Managament/components/tabs/bookingTabs/identityTab/BookingHistoryTab';
import InmatesTab from '@/pages/JMS/Managament/components/tabs/mainTabs/InmatesTab';
import ManagementLayout from '@/pages/JMS/Managament/ManagementLayout';
import KaseReportListScreen from '@/pages/Kases/KaseReports/KaseLogsListScreen';
import KeyboardShortcuts from '@/pages/KeyboardShortcuts/KeyboardShortcut';
import LocationMarkers from '@/pages/LocationMarkers/LocationMarkers';
import MasterAddressNotesView from '@/pages/MasterIndex/Address/components/MasterAddressNotesView';
import { MasterIndexAddressViewContent } from '@/pages/MasterIndex/Address/components/MasterIndexAddressViewContent';
import { MasterIndexDetailsPage } from '@/pages/MasterIndex/components/MasterIndexDetailsPage';
import MasterIndexRelatedRecords from '@/pages/MasterIndex/components/MasterIndexRelatedRecords/MasterIndexRelatedRecords';
import { MasterIndex } from '@/pages/MasterIndex/MasterIndex';
import MasterIndexOrganizationForm from '@/pages/MasterIndex/Organizations/Form';
import MasterIndexOrganizationNotes from '@/pages/MasterIndex/Organizations/Notes';
import MasterIndexOrganizationView from '@/pages/MasterIndex/Organizations/View';
import MasterPersonsDataGrid from '@/pages/MasterIndex/Persons/components/MasterPersonsDataGrid';
import { masterPersonsDataGridCols } from '@/pages/MasterIndex/Persons/data';
import MasterIndexPersonFlags from '@/pages/MasterIndex/Persons/Flags';
import MasterIndexPersonForm from '@/pages/MasterIndex/Persons/Form';
import { MasterIndexPersonNotes } from '@/pages/MasterIndex/Persons/Notes';
import MasterIndexPersonView from '@/pages/MasterIndex/Persons/View';
import MasterVehicleEdit from '@/pages/MasterIndex/Vehicles/components/MasterVehicleEdit';
import MasterIndexVehicleView from '@/pages/MasterIndex/Vehicles/components/MasterVehicleView';
import MasterIndexVehiclesNotes from '@/pages/MasterIndex/Vehicles/MasterIndexVehiclesNotes';
import NCICSearchHistory from '@/pages/NCIC/NCICSearchHistory/NCICSearchHistory';
import NCICSearchPage from '@/pages/NCIC/NCICSearchPage/NCICSearchPage';
import PropertyEvidencePage from '@/pages/Properties/PropertyEvidencePage';
import SettingsPage from '@/pages/Settings/SettingsPage';
import ArrestsToolDataGrid from '@/pages/Tools/Arrests/ArrestsToolDataGrid';
import BuildingInspection from '@/pages/Tools/BuildingInspection/BuildingInspection';
import { BuildingLayout } from '@/pages/Tools/BuildingInspection/BuildingLayout';
import BuildingRecord from '@/pages/Tools/BuildingInspection/BuildingRecord';
import StartInspection from '@/pages/Tools/BuildingInspection/StartInspection';
import ViewInspection from '@/pages/Tools/BuildingInspection/ViewInspection';
import DomesticIncidentReportList from '@/pages/Tools/DomesticIncidentReport/DomesticIncidentReportList';
import InternalAffairsAddEditPage from '@/pages/Tools/InternalAffairs/InternalAffairsAddEditPage';
import { InternalAffairsAuditTrail } from '@/pages/Tools/InternalAffairs/InternalAffairsAuditTrail';
import InternalAffairsList from '@/pages/Tools/InternalAffairs/InternalAffairsList';
import { InternalAffairsOverviewForm } from '@/pages/Tools/InternalAffairs/InternalAffairsOverviewForm';
import { InternalAffairsPeopleList } from '@/pages/Tools/InternalAffairs/InternalAffairsPeopleList';
import { InternalAffairsRelatedRecords } from '@/pages/Tools/InternalAffairs/InternalAffairsRelatedRecords';
import { InternalAffairsView } from '@/pages/Tools/InternalAffairs/InternalAffairsView';
import InternalAffairsViewPage from '@/pages/Tools/InternalAffairs/InternalAffairsViewPage';
import ToolsManagement from '@/pages/Tools/ToolsManagement';
import { TowingCompanyDetailsPage } from '@/pages/Tools/TowingManagement/Details/TowingCompanyDetails';
import { TowRecordDetailsPage } from '@/pages/Tools/TowingManagement/Details/TowRecordDetails';
import TowingManagement from '@/pages/Tools/TowingManagement/TowingManagement';
import { UnitManagement } from '@/pages/Units/UnitManagement';

import {
  AuthGuard,
  CallLogGuard,
  DemoGuard,
  GuestGuard,
  InternalAffairsGuard,
  MasterIndexIndexRedirect,
  RequireMasterIndexAccess,
  RequireMasterIndexSection,
} from './RouteGuards';
import {
  EmployeeDetailsTabRoutes,
  MainRoutes,
  MasterIndexRoutes,
  MasterIndexTabRoutes,
} from './RoutePaths';

// Location Markers screens
const LocationMarkersPage = lazy(
  () => import('@/pages/LocationMarkers/LocationMarkersPage'),
);

// Auth screens
const Login = lazy(() => import('@/pages/Auth/Login'));
const ForgotPassword = lazy(() => import('@/pages/Auth/ForgotPassword'));

const ResetPassword = lazy(() => import('@/pages/Auth/ResetPassword'));
const EmailConfirm = lazy(() => import('@/pages/Auth/EmailConfirm'));
const CodeVerification = lazy(() => import('@/pages/Auth/CodeVerification'));

// Dashboard screens
const AgencyList = lazy(() => import('@/pages/AgencyList'));
const AgencyDetail = lazy(() => import('@/pages/AgencyDetail'));

const EmployeeList = lazy(() => import('@/pages/Employees/List'));
const EmployeeEdit = lazy(() => import('@/pages/Employees/Edit'));
const EmployeeView = lazy(() => import('@/pages/Employees/View'));
const VehicleList = lazy(() => import('@/pages/Vehicles/List'));
const VehicleEdit = lazy(() => import('@/pages/Vehicles/Edit'));
const VehicleView = lazy(() => import('@/pages/Vehicles/View'));
const EquipmentList = lazy(() => import('@/pages/Equipments/List'));
const NCICSearchRequest = lazy(
  () => import('@/pages/NCIC/NCICSearchRequests/NCICSearchRequestsPage'),
);
const EquipmentEdit = lazy(() => import('@/pages/Equipments/Edit'));
const EquipmentView = lazy(() => import('@/pages/Equipments/View'));
const UnitList = lazy(() => import('@/pages/Units/List'));
const UnitShiftHistoryList = lazy(
  () => import('@/pages/UnitShifts/UnitShiftsHistory'),
);
const UnitEdit = lazy(() => import('@/pages/Units/Edit'));
const UnitView = lazy(() => import('@/pages/Units/View'));
const UnitTypeList = lazy(() => import('@/pages/UnitType/UnitTypeList'));
const UnitTypeEdit = lazy(() => import('@/pages/UnitType/UnitTypeEdit'));
const UnitTypeView = lazy(() => import('@/pages/UnitType/UnitTypeView'));
const UnitShiftList = lazy(() => import('@/pages/Units/UnitShifts'));
const MasterUnitShiftList = lazy(() => import('@/pages/UnitShifts/List'));
const VendorList = lazy(() => import('@/pages/Vendors/VendorList'));
const VendorView = lazy(() => import('@/pages/Vendors/VendorView'));
const VendorEdit = lazy(() => import('@/pages/Vendors/VendorEdit'));
const VendorHistory = lazy(() => import('@/pages/Vendors/VendorHistory'));
const InventoryLocationsList = lazy(
  () => import('@/pages/InventoryLocations/InventoryLocationsList'),
);
const InventoryLocationsEdit = lazy(
  () => import('@/pages/InventoryLocations/InventoryLocationsEdit'),
);
const InventoryLocationsView = lazy(
  () => import('@/pages/InventoryLocations/InventoryLocationsView'),
);
const AgencySettings = lazy(() => import('@/pages/AgencySettings'));
const HelpView = lazy(() => import('@/pages/Help/HelpView'));
const ReleaseNoteView = lazy(() => import('@/pages/Help/ReleaseNoteView'));

// Analytics screens
const AnalyticsPage = lazy(() => import('@/pages/Analytics'));

// CFS screens
const CFSList = lazy(loadCFSListRoute);
const ActiveCFSList = lazy(loadActiveCFSListRoute);
const CFSDetail = lazy(() => import('@/pages/CFS/Edit'));
const CFSHistory = lazy(() => import('@/pages/CFS/CFSHistory'));
const CFSNames = lazy(() => import('@/pages/CFS/CFSNames'));
const CFSProperties = lazy(() => import('@/pages/CFS/CFSProperties'));
const CFSVehicles = lazy(() => import('@/pages/CFS/CFSVehicles'));
const CFS911Info = lazy(() => import('@/pages/CFS/Information911'));
const CFSProcedure = lazy(
  () => import('@/pages/CFS/CFSRecommendedProcedure/Procedure'),
);
const CFSNarratives = lazy(() => import('@/pages/CFS/CFSNarratives'));
const CFSCallerCommunication = lazy(
  () => import('@/pages/CFS/CFSCallerCommunication/CFSCallerCommunication'),
);
const CFSFilesAndForms = lazy(() => import('@/pages/CFS/CFSFilesAndForms'));
const CFSRelatedRecords = lazy(
  () => import('@/pages/CFS/CFSRelatedRecords/CFSRelatedRecords'),
);
const NewWindowLiveInfo = lazy(
  () => import('@/pages/CFS/Edit/components/NewWindowLiveInfo'),
);
const CFSFirstRespondersPage = lazy(
  () => import('@/pages/FirstResponder/pages/MyCfs/MyCfsPage'),
);

const LegacyCFSSlideRedirect = ({
  basePath,
}: {
  basePath: 'cfs' | 'cfs-active';
}) => {
  const { cfsId, '*': childPath } = useParams();
  const { search } = useLocation();

  if (!cfsId) {
    return <Navigate to={`/${basePath}`} replace />;
  }

  const suffix = childPath ? `/${childPath}` : '';
  return <Navigate to={`/${basePath}/${cfsId}${suffix}${search}`} replace />;
};

// Kases screens
const KaseList = lazy(() => import('@/pages/Kases/KaseList/KaseList'));
const ImportedKaseList = lazy(
  () => import('@/pages/Kases/ImportedKaseList/KaseList'),
);
const RecordsList = lazy(() => import('@/pages/CFS/Records/RecordsList'));
const KaseEdit = lazy(() => import('@/pages/Kases/KaseEdit/KaseEdit'));
const KaseNames = lazy(() => import('@/pages/Kases/KaseNames/KaseNames'));
const KaseVehicles = lazy(
  () => import('@/pages/Kases/KaseVehicles/KaseVehicles'),
);
const KaseProperty = lazy(
  () => import('@/pages/Kases/KaseProperty/KaseProperty'),
);
const KaseNaratives = lazy(
  () => import('@/pages/Kases/KaseNaratives/KaseNaratives'),
);
const KaseFiles = lazy(() => import('@/pages/Kases/KaseFiles/KaseFiles'));
const KaseOverview = lazy(
  () => import('@/pages/Kases/KaseOverview/KaseOverview'),
);
const KaseCrimes = lazy(() => import('@/pages/Kases/KaseCrimes/KaseCrimes'));
const KaseArrests = lazy(() => import('@/pages/Kases/KaseArrests/KaseArrests'));
const KaseLogListScreen = lazy(
  () => import('@/pages/Kases/KaseLogs/KaseLogsListScreen'),
);

const NibrsScreen = lazy(() => import('@/pages/Kases/NIBRS/NibrsScreen'));
// Fire screens
const FireKaseListPage = lazy(
  () => import('@/pages/FireKases/FireKaseList/FireKaseList'),
);

const FireKaseEditPage = lazy(
  () => import('@/pages/FireKases/FireKaseEdit/FireKaseEdit'),
);

// Tasks screens
const TaskList = lazy(() => import('@/pages/Tasks/TaskList/TaskList'));
const TaskEdit = lazy(() => import('@/pages/Tasks/TaskEdit/TaskEdit'));
const TaskView = lazy(() => import('@/pages/Tasks/TaskView/TaskView'));

// Operations screens
const MasterPersonSearch = lazy(
  () =>
    import(
      '@/pages/JMS/Managament/components/tabs/bookingTabs/identityTab/MasterPersonSearch'
    ),
);

const BookingLoader = lazy(
  () => import('@/pages/JMS/Managament/components/BookingLoader'),
);

const IdentityTab = lazy(
  () =>
    import(
      '@/pages/JMS/Managament/components/tabs/bookingTabs/identityTab/IdentityTab'
    ),
);

const BookingTab = lazy(
  () => import('@/pages/JMS/Managament/components/tabs/bookingTabs/BookingTab'),
);

const FingerprintsTab = lazy(
  () =>
    import(
      '@/pages/JMS/Managament/components/tabs/bookingTabs/FingerprintsTab'
    ),
);

const InmatesMovementLoader = lazy(
  () =>
    import(
      '@/pages/JMS/Operations/InmatesMovement/components/InmatesMovementLoader'
    ),
);

const HousingTab = lazy(
  () => import('@/pages/JMS/Managament/components/tabs/bookingTabs/HousingTab'),
);
const InmateHousingTab = lazy(
  () =>
    import(
      '@/pages/JMS/Managament/components/tabs/inmateTabs/InmateHousingTab'
    ),
);

// Tools screens
const ComplaintsList = lazy(
  () => import('@/pages/Tools/Complaints/ComplaintsList'),
);
const SexOffendersList = lazy(
  () => import('@/pages/Tools/SexOffenders/SexOffendersList'),
);
const LawLibraryList = lazy(
  () => import('@/pages/Tools/LawLibrary/LawLibraryList'),
);
const BOLOsList = lazy(() => import('@/pages/Tools/BOLOs/BOLOsList'));
const CameraList = lazy(() => import('@/pages/Tools/Cameras/CameraList'));
const CameraEdit = lazy(() => import('@/pages/Tools/Cameras/CameraEdit'));
const CameraView = lazy(() => import('@/pages/Tools/Cameras/CameraView'));
const PermitList = lazy(
  () => import('@/pages/Tools/Permits/PermitList/PermitList'),
);
const WantedPersonList = lazy(
  () => import('@/pages/Tools/WantedPerson/WantedPersonList'),
);

const WantedPersonAddEditPage = lazy(
  () => import('@/pages/Tools/WantedPerson/WantedPersonAddEditPage'),
);

const WantedPersonViewPage = lazy(
  () => import('@/pages/Tools/WantedPerson/WantedPersonViewPage'),
);

const VehicleMaintenanceList = lazy(
  () => import('@/pages/Tools/VehicleMaintenance/VehicleMaintenanceList'),
);
const VehicleMaintenanceViewPage = lazy(
  () => import('@/pages/Tools/VehicleMaintenance/VehicleMaintenanceViewPage'),
);

const CalendarScheduler = lazy(
  () => import('@/pages/CalendarScheduler/CalendarScheduler'),
);

const MasterIndexOrganizationsList = lazy(
  () => import('@/pages/MasterIndex/Organizations/List'),
);
const MasterIndexVehiclesList = lazy(
  () => import('@/pages/MasterIndex/Vehicles/List'),
);
const MasterIndexPropertiesList = lazy(
  () => import('@/pages/MasterIndex/Properties/List'),
);
const MasterIndexPropertiesView = lazy(
  () => import('@/pages/MasterIndex/Properties/View'),
);
const MasterIndexAddressList = lazy(
  () => import('@/pages/MasterIndex/Address/List'),
);
const MasterIndexAddressEdit = lazy(
  () => import('@/pages/MasterIndex/Address/Edit'),
);

const MasterIndexLogsList = lazy(() => import('@/pages/MasterIndex/Logs/List'));

// Error pages
const NotFoundScreen = lazy(() => import('@/pages/NotFound'));
const ForbiddenScreen = lazy(() => import('@/pages/Forbidden'));

// Re-adding JMS imports that might have been removed, ensure correct alphabetical/group order

export const routes: RouteObject[] = [
  {
    path: '/auth',
    element: (
      <GuestGuard>
        <AuthLayout />
      </GuestGuard>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="/auth/login" replace />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPassword />,
      },
      {
        path: 'reset-password',
        element: <ResetPassword />,
      },
      {
        path: 'confirm-email',
        element: <EmailConfirm />,
      },
      {
        path: 'code-verification',
        element: <CodeVerification />,
      },
      {
        path: 'login',
        element: <Login />,
      },
    ],
  },
  {
    path: 'cfs/:cfsId/live-info',
    element: (
      <AuthGuard>
        <NewWindowLiveInfo />
      </AuthGuard>
    ),
  },
  {
    path: 'unit-shifts',
    element: (
      <AuthGuard>
        <MainLayout isFullScreen={true} />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <UnitShiftList />,
      },
    ],
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <MainLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="/cfs-active" replace />,
      },
      {
        path: 'ncic-search',
        handle: {
          allowedRoles: [Role.RMS_CASES_FULL_ACCESS, Role.RMS_CASES_VIEW],
          allowedAgencies: [AgencyType.POLICE],
        },
        children: [
          {
            index: true,
            element: <NCICSearchPage />,
          },
          {
            path: 'history',
            element: <NCICSearchHistory />,
          },
          {
            path: 'history/:searchId',
            element: <NCICSearchHistory />,
          },
        ],
      },
      {
        path: 'admin',
        element: <Navigate to="/admin/agencies" replace />,
      },
      {
        path: 'admin',
        element: <AdminLayout />,
        handle: {
          allowedRoles: [Role.ACCOUNT_ADMIN, Role.AGENCY_ADMIN],
        },
        children: [
          {
            path: 'agencies',
            element: <AgencyList />,
          },
          {
            path: 'audit-logs',
            element: <AuditLogsPage />,
            handle: {
              allowedRoles: [Role.AGENCY_ADMIN],
            },
          },
          {
            path: 'account-configuration/incident-codes',
            element: <IncidentCodesPage />,
            handle: {
              allowedRoles: [Role.ACCOUNT_ADMIN],
            },
          },
          {
            path: 'account-configuration/incident-codes/new',
            element: <AddIncidentCodePage />,
            handle: {
              allowedRoles: [Role.ACCOUNT_ADMIN],
            },
          },
          {
            path: 'account-configuration/incident-codes/edit/:incidentCodeId',
            element: <EditIncidentCodePage />,
            handle: {
              allowedRoles: [Role.ACCOUNT_ADMIN],
            },
          },
          {
            path: 'account-configuration/incident-codes/view/:incidentCodeId',
            element: <ViewIncidentCodePage />,
            handle: {
              allowedRoles: [Role.ACCOUNT_ADMIN],
            },
          },
          {
            path: 'account-configuration/location-markers',
            element: <LocationMarkersPage />,
            handle: {
              allowedRoles: [Role.ACCOUNT_ADMIN],
            },
          },
        ],
      },
      {
        path: 'settings',
        element: <SettingsPage />,
      },
      {
        path: 'settings/profile',
        element: <SettingsPage />,
      },
      {
        path: 'settings/cfs',
        element: <SettingsPage />,
      },
      {
        path: 'settings/notifications',
        element: <SettingsPage />,
      },
      {
        path: 'help',
        element: <HelpPage />,
      },
      {
        path: 'help/release-notes',
        element: <HelpView />,
      },
      {
        path: 'help/release-notes/:id',
        element: <ReleaseNoteView />,
      },
      {
        path: 'analytics',
        element: <AnalyticsPage />,
      },
      {
        path: 'agencies/:agencyId',
        element: <AgencyDetail />,
        children: [
          {
            index: true,
            element: <Navigate to="employees" replace />,
          },
          {
            path: 'employees',
            element: <EmployeeList />,
          },
          {
            path: 'vehicles',
            element: <VehicleList />,
          },
          {
            path: 'equipment',
            element: <EquipmentList />,
          },
          {
            path: `ncic-requests`,
            element: <NCICSearchRequest />,
          },
          {
            path: 'unit-management',
            element: <UnitManagement />,
            children: [
              {
                index: true,
                element: <NavigatePreserveSearch to="units" replace />,
              },
              {
                path: 'units',
                element: <UnitList />,
                children: [
                  {
                    path: 'unit-shifts',
                    element: <UnitShiftListSideModal open={true} />,
                  },
                ],
              },
              {
                path: 'types',
                element: <UnitTypeList />,
              },
              {
                path: 'assignments',
                element: <MasterUnitShiftList />,
              },
              {
                path: 'history',
                element: <UnitShiftHistoryList />,
                children: [
                  {
                    path: 'cfs/:cfsId',
                    element: (
                      <UnitShiftListModal
                        fetchActiveCFSList={() => {
                          /* fetch active CFS list */
                        }}
                        backRoute=".."
                        open={true}
                      />
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'vendors',
            element: <VendorList />,
          },
          {
            path: 'inventory-locations',
            element: <InventoryLocationsList />,
            handle: {
              allowedRoles: [Role.AGENCY_ADMIN, Role.ACCOUNT_ADMIN],
            },
          },
          {
            path: 'agency-settings',
            element: <AgencySettings />,
            handle: {
              allowedRoles: [Role.AGENCY_ADMIN, Role.ACCOUNT_ADMIN],
            },
          },
        ],
      },
      {
        path: `agencies/${MainRoutes.EmployeeList}/:employeeId/view`,
        element: <EmployeeDetailsPage />,
        children: [
          {
            index: true,
            element: (
              <Navigate to={EmployeeDetailsTabRoutes.Information} replace />
            ),
          },
          {
            path: EmployeeDetailsTabRoutes.Information,
            element: <EmployeeView />,
          },
          {
            path: EmployeeDetailsTabRoutes.Devices,
            element: <EmployeeDevices />,
          },
        ],
      },
      {
        path: `agencies/${MainRoutes.EmployeeList}/:employeeId`,
        element: <EmployeeEdit />,
      },
      {
        path: `agencies/${MainRoutes.VehicleList}/:vehicleId/view`,
        element: <VehicleView />,
      },
      {
        path: `agencies/${MainRoutes.VehicleList}/:vehicleId`,
        element: <VehicleEdit />,
      },
      {
        path: `agencies/${MainRoutes.EquipmentList}/:equipmentId/view`,
        element: <EquipmentView />,
      },
      {
        path: `agencies/${MainRoutes.EquipmentList}/:equipmentId`,
        element: <EquipmentEdit />,
      },
      {
        path: `agencies/${MainRoutes.UnitList}/:unitId/view`,
        element: <UnitView />,
      },
      {
        path: `agencies/${MainRoutes.UnitList}/:unitId`,
        element: <UnitEdit />,
      },
      {
        path: `agencies/${MainRoutes.UnitTypeList}/:unitTypeId`,
        element: <UnitTypeEdit />,
      },
      {
        path: `agencies/${MainRoutes.UnitTypeList}/:unitTypeId/view`,
        element: <UnitTypeView />,
      },
      {
        path: `agencies/${MainRoutes.VendorList}/:vendorId/view`,
        element: <VendorView />,
      },
      {
        path: `agencies/${MainRoutes.VendorList}/:vendorId`,
        element: <VendorEdit />,
      },
      {
        path: `agencies/${MainRoutes.VendorList}/:vendorId/history`,
        element: <VendorHistory />,
      },
      {
        path: `agencies/${MainRoutes.InventoryLocations}/:locationId/view`,
        element: <InventoryLocationsView />,
      },
      {
        path: `agencies/${MainRoutes.InventoryLocations}/:locationId`,
        element: <InventoryLocationsEdit />,
      },
      {
        path: 'cfs',
        element: <CFSList />,
        children: [
          {
            path: 'slide/:cfsId/*',
            element: <LegacyCFSSlideRedirect basePath="cfs" />,
          },
        ],
      },
      {
        path: 'cfs/:cfsId',
        element: <CFSDetail />,
        children: [
          {
            path: 'names',
            element: <CFSNames />,
          },
          {
            path: 'vehicles',
            element: <CFSVehicles />,
          },
          {
            path: 'properties',
            element: <CFSProperties />,
          },
          {
            path: '911-information',
            element: <CFS911Info />,
          },
          {
            path: 'procedure',
            element: <CFSProcedure />,
          },
          {
            path: 'media',
            element: <CFSMedia />,
          },
          {
            path: 'files-and-forms',
            element: <CFSFilesAndForms />,
          },
          {
            path: 'history',
            element: <CFSHistory />,
          },
          {
            path: 'timeline',
            element: <CFSTimeline />,
          },
          {
            path: 'caller-communication',
            element: <CFSCallerCommunication />,
          },
          {
            path: 'narratives',
            element: <CFSNarratives />,
          },
          {
            path: 'related-records',
            element: <CFSRelatedRecords />,
          },
          {
            path: 'carelink',
            element: <CFSCareLinkChatTab />,
          },
        ],
      },
      {
        path: 'cfs-active',
        element: <ActiveCFSList />,
        children: [
          {
            path: 'unit-shifts',
            element: (
              <UnitShiftListSideModal backRoute="/cfs-active" open={true} />
            ),
          },
          {
            path: 'carelink',
            element: <CareLinkModal open={true} />,
          },
          {
            path: 'slide/:cfsId/*',
            element: <LegacyCFSSlideRedirect basePath="cfs-active" />,
          },
        ],
      },
      {
        path: 'cfs-active/:cfsId',
        element: <CFSDetail />,
        children: [
          {
            path: 'names',
            element: <CFSNames />,
          },
          {
            path: 'vehicles',
            element: <CFSVehicles />,
          },
          {
            path: 'properties',
            element: <CFSProperties />,
          },
          {
            path: '911-information',
            element: <CFS911Info />,
          },
          {
            path: 'procedure',
            element: <CFSProcedure />,
          },
          {
            path: 'media',
            element: <CFSMedia />,
          },
          {
            path: 'files-and-forms',
            element: <CFSFilesAndForms />,
          },
          {
            path: 'history',
            element: <CFSHistory />,
          },
          {
            path: 'timeline',
            element: <CFSTimeline />,
          },
          {
            path: 'caller-communication',
            element: <CFSCallerCommunication />,
          },
          {
            path: 'narratives',
            element: <CFSNarratives />,
          },
          {
            path: 'related-records',
            element: <CFSRelatedRecords />,
          },
          {
            path: 'carelink',
            element: <CFSCareLinkChatTab />,
          },
        ],
      },
      {
        path: 'call-records',
        element: (
          <CallLogGuard>
            <CallRecordsList />
          </CallLogGuard>
        ),
      },
      {
        path: 'cases-imported',
        children: [
          {
            index: true,
            element: <ImportedKaseList />,
          },
          {
            path: ':kaseId',
            element: <KaseEdit />,
            children: [
              {
                index: true,
                element: <KaseEdit />,
              },
              {
                path: 'overview',
                element: <KaseOverview />,
              },
              {
                path: 'names',
                element: <KaseNames />,
              },
              {
                path: 'vehicles',
                element: <KaseVehicles />,
              },
              {
                path: 'properties',
                element: <KaseProperty />,
              },
              {
                path: 'narrative',
                element: <KaseNaratives />,
              },
              {
                path: 'files',
                element: <KaseFiles />,
              },
              {
                path: 'crimes',
                element: <KaseCrimes />,
              },
              {
                path: 'arrests',
                element: <KaseArrests />,
              },
              {
                path: 'logs',
                element: <KaseLogListScreen />,
              },
              {
                path: 'reports',
                element: (
                  <DemoGuard>
                    <KaseReportListScreen />
                  </DemoGuard>
                ),
              },
            ],
          },
          {
            path: 'records',
            element: <RecordsList />,
          },
          {
            path: 'cfs',
            element: <KaseList />,
            children: [
              {
                path: ':cfsId',
                element: (
                  <UnitShiftListModal
                    fetchActiveCFSList={() => {
                      /* fetch active CFS list */
                    }}
                    open={true}
                    backRoute="/cases"
                  />
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'cases',
        children: [
          {
            index: true,
            element: <KaseList />,
          },
          {
            path: ':kaseId',
            element: <KaseEdit />,
            children: [
              {
                index: true,
                element: <KaseEdit />,
              },
              {
                path: 'overview',
                element: <KaseOverview />,
              },
              {
                path: 'names',
                element: <KaseNames />,
              },
              {
                path: 'vehicles',
                element: <KaseVehicles />,
              },
              {
                path: 'properties',
                element: <KaseProperty />,
              },
              {
                path: 'narrative',
                element: <KaseNaratives />,
              },
              {
                path: 'files',
                element: <KaseFiles />,
              },
              {
                path: 'crimes',
                element: <KaseCrimes />,
              },
              {
                path: 'arrests',
                element: <KaseArrests />,
              },
              {
                path: 'logs',
                element: <KaseLogListScreen />,
              },
              {
                path: 'nibrs',
                element: <NibrsScreen />,
              },

              {
                path: 'reports',
                element: (
                  <DemoGuard>
                    <KaseReportListScreen />
                  </DemoGuard>
                ),
              },
            ],
          },
          {
            path: 'records',
            element: <RecordsList />,
          },
          {
            path: 'cfs',
            element: <KaseList />,
            children: [
              {
                path: ':cfsId',
                element: (
                  <UnitShiftListModal
                    fetchActiveCFSList={() => {
                      /* fetch active CFS list */
                    }}
                    open={true}
                    backRoute="/cases"
                  />
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'fire-cases',
        children: [
          {
            index: true,
            element: <FireKaseListPage />,
          },
          {
            path: ':kaseId',
            element: <FireKaseEditPage />,
            children: [
              {
                index: true,
                element: <FireKaseEditPage />,
              },
            ],
          },
        ],
      },
      {
        path: 'evidence',
        element: <PropertyEvidencePage />,
      },
      {
        path: 'master-index',
        element: (
          <RequireMasterIndexAccess>
            <MasterIndex />
          </RequireMasterIndexAccess>
        ),
        children: [
          { index: true, element: <MasterIndexIndexRedirect /> },

          {
            path: MasterIndexRoutes.Persons,
            element: (
              <RequireMasterIndexSection section="persons">
                <Outlet />
              </RequireMasterIndexSection>
            ),
            children: [
              {
                index: true,
                element: (
                  <MasterPersonsDataGrid
                    name="master-persons"
                    dataGridCols={masterPersonsDataGridCols}
                  />
                ),
              },
              {
                path: 'new',
                element: (
                  <MasterIndexDetailsPage
                    action="new"
                    masterIndexType={MasterIndexRoutes.Persons}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <NavigatePreserveSearch
                        to={MasterIndexTabRoutes.Information}
                        replace
                      />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Information,
                    element: <MasterIndexPersonForm />,
                  },
                  {
                    path: MasterIndexTabRoutes.Related,
                    element: (
                      <MasterIndexRelatedRecords
                        type={MasterIndexApiPath.People}
                      />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Notes,
                    element: <MasterIndexPersonNotes />,
                  },
                ],
              },
              {
                path: ':id/view',
                element: (
                  <MasterIndexDetailsPage
                    action="view"
                    masterIndexType="persons"
                  />
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <NavigatePreserveSearch to="information" replace />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Information,
                    element: <MasterIndexPersonView />,
                  },
                  {
                    path: MasterIndexTabRoutes.Related,
                    element: (
                      <MasterIndexRelatedRecords
                        type={MasterIndexApiPath.People}
                      />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Flags,
                    element: <MasterIndexPersonFlags />,
                  },
                  {
                    path: MasterIndexTabRoutes.Notes,
                    element: <MasterIndexPersonNotes />,
                  },
                  {
                    path: 'cfs-history',
                    element: <CFSInvolvementHistory />,
                  },
                ],
              },
              {
                path: ':id/edit',
                element: (
                  <MasterIndexDetailsPage
                    action="edit"
                    masterIndexType="persons"
                  />
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <NavigatePreserveSearch to="information" replace />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Information,
                    element: <MasterIndexPersonForm />,
                  },
                  {
                    path: MasterIndexTabRoutes.Related,
                    element: (
                      <MasterIndexRelatedRecords
                        type={MasterIndexApiPath.People}
                      />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Flags,
                    element: <MasterIndexPersonFlags />,
                  },
                  {
                    path: MasterIndexTabRoutes.Notes,
                    element: <MasterIndexPersonNotes />,
                  },
                ],
              },
            ],
          },
          {
            path: MasterIndexRoutes.Organizations,
            element: (
              <RequireMasterIndexSection section="organizations">
                <Outlet />
              </RequireMasterIndexSection>
            ),
            children: [
              {
                index: true,
                element: <MasterIndexOrganizationsList />,
              },
              {
                path: 'new',
                element: (
                  <MasterIndexDetailsPage
                    action="new"
                    masterIndexType={MasterIndexRoutes.Organizations}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <NavigatePreserveSearch
                        to={MasterIndexTabRoutes.Information}
                        replace
                      />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Information,
                    element: <MasterIndexOrganizationForm />,
                  },
                  {
                    path: MasterIndexTabRoutes.Related,
                    element: <></>,
                  },
                  {
                    path: MasterIndexTabRoutes.Notes,
                    element: <></>,
                  },
                ],
              },
              {
                path: ':id/view',
                element: (
                  <MasterIndexDetailsPage
                    action="view"
                    masterIndexType="organizations"
                  />
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <NavigatePreserveSearch to="information" replace />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Information,
                    element: <MasterIndexOrganizationView />,
                  },
                  {
                    path: MasterIndexTabRoutes.Related,
                    element: (
                      <MasterIndexRelatedRecords
                        type={MasterIndexApiPath.Organizations}
                      />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Notes,
                    element: <MasterIndexOrganizationNotes />,
                  },
                ],
              },
              {
                path: ':id/edit',
                element: (
                  <MasterIndexDetailsPage
                    action="edit"
                    masterIndexType="organizations"
                  />
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <NavigatePreserveSearch to="information" replace />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Information,
                    element: <MasterIndexOrganizationForm />,
                  },
                  {
                    path: MasterIndexTabRoutes.Related,
                    element: (
                      <MasterIndexRelatedRecords
                        type={MasterIndexApiPath.Organizations}
                      />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Notes,
                    element: <MasterIndexOrganizationNotes />,
                  },
                ],
              },
            ],
          },
          {
            path: MasterIndexRoutes.Vehicles,
            element: (
              <RequireMasterIndexSection section="vehicles">
                <Outlet />
              </RequireMasterIndexSection>
            ),
            children: [
              {
                index: true,
                element: <MasterIndexVehiclesList />,
              },
              {
                path: 'new',
                element: (
                  <MasterIndexDetailsPage
                    action="new"
                    masterIndexType={MasterIndexRoutes.Vehicles}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <NavigatePreserveSearch
                        to={MasterIndexTabRoutes.Information}
                        replace
                      />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Information,
                    element: <MasterVehicleEdit />,
                  },
                  {
                    path: MasterIndexTabRoutes.Notes,
                    element: <MasterIndexVehiclesNotes />,
                  },
                ],
              },
              {
                path: ':vehicleId',
                element: (
                  <MasterIndexDetailsPage
                    action="view"
                    masterIndexType={MasterIndexRoutes.Vehicles}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <NavigatePreserveSearch
                        to={MasterIndexTabRoutes.Information}
                        replace
                      />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Information,
                    element: <MasterIndexVehicleView />,
                  },
                  {
                    path: MasterIndexTabRoutes.Related,
                    element: (
                      <MasterIndexRelatedRecords
                        idKey="vehicleId"
                        type={MasterIndexApiPath.Vehicles}
                      />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Notes,
                    element: <MasterIndexVehiclesNotes />,
                  },
                ],
              },
              {
                path: ':vehicleId/edit',
                element: (
                  <MasterIndexDetailsPage
                    action="edit"
                    masterIndexType={MasterIndexRoutes.Vehicles}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <NavigatePreserveSearch
                        to={MasterIndexTabRoutes.Information}
                        replace
                      />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Information,
                    element: <MasterVehicleEdit />,
                  },
                  {
                    path: MasterIndexTabRoutes.Related,
                    element: (
                      <MasterIndexRelatedRecords
                        idKey="vehicleId"
                        type={MasterIndexApiPath.Vehicles}
                      />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Notes,
                    element: <MasterIndexVehiclesNotes />,
                  },
                ],
              },
            ],
          },
          {
            path: MasterIndexRoutes.Properties,
            element: (
              <RequireMasterIndexSection section="properties">
                <Outlet />
              </RequireMasterIndexSection>
            ),
            children: [
              {
                index: true,
                element: <MasterIndexPropertiesList />,
              },
              {
                path: ':propertyId',
                element: <MasterIndexPropertiesView />,
              },
            ],
          },
          {
            path: MasterIndexRoutes.Address,
            element: (
              <RequireMasterIndexSection section="address">
                <Outlet />
              </RequireMasterIndexSection>
            ),
            children: [
              {
                index: true,
                element: <MasterIndexAddressList />,
              },
              {
                path: ':addressId/view',
                element: (
                  <MasterIndexDetailsPage
                    action="view"
                    masterIndexType="address"
                  />
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <NavigatePreserveSearch to="information" replace />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Information,
                    element: <MasterIndexAddressViewContent />,
                  },
                  {
                    path: MasterIndexTabRoutes.Related,
                    element: (
                      <MasterIndexRelatedRecords
                        idKey="addressId"
                        type={MasterIndexApiPath.Addresses}
                      />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.AddressNotes,
                    element: <MasterAddressNotesView />,
                  },
                ],
              },
              {
                path: ':addressId/edit',
                element: (
                  <MasterIndexDetailsPage
                    action="edit"
                    masterIndexType="address"
                  />
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <NavigatePreserveSearch to="information" replace />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Information,
                    element: <MasterIndexAddressEdit />,
                  },
                  {
                    path: MasterIndexTabRoutes.Related,
                    element: (
                      <MasterIndexRelatedRecords
                        idKey="addressId"
                        type={MasterIndexApiPath.Addresses}
                      />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.AddressNotes,
                    element: <MasterAddressNotesView />,
                  },
                ],
              },
              {
                path: 'new',
                element: (
                  <MasterIndexDetailsPage
                    action="new"
                    masterIndexType="address"
                  />
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <NavigatePreserveSearch to="information" replace />
                    ),
                  },
                  {
                    path: MasterIndexTabRoutes.Information,
                    element: <MasterIndexAddressEdit />,
                  },
                ],
              },
            ],
          },
          {
            path: MasterIndexRoutes.Logs,
            element: (
              <RequireMasterIndexSection section="logs">
                <Outlet />
              </RequireMasterIndexSection>
            ),
            children: [
              {
                index: true,
                element: <MasterIndexLogsList />,
              },
            ],
          },
        ],
      },
      {
        path: 'tasks',
        children: [
          {
            index: true,
            element: <TaskList />,
          },

          {
            path: `${MainRoutes.TaskList}/:taskId/view`,
            element: <TaskView />,
          },
          {
            path: `${MainRoutes.TaskList}/:taskId`,
            element: <TaskEdit />,
          },
        ],
      },
      {
        path: 'jms/management',
        element: <ManagementLayout />,
        children: [
          {
            index: true,
            element: <InmatesTab />,
          },
          {
            path: 'bookings',
            element: <BookingDataGrid />,
          },
          {
            path: 'search-person',
            index: true,
            element: <MasterPersonSearch />,
          },
          {
            path: 'create',
            element: <BookingLoader />,
            children: [
              {
                path: 'identity',
                element: <IdentityTab />,
              },
              {
                path: 'booking',
                element: <BookingTab />,
              },
            ],
          },
          {
            path: 'booking/:bookingId',
            element: <BookingLoader />,
            children: [
              {
                path: 'identity',
                element: <IdentityTab />,
              },
              {
                path: 'booking',
                element: <BookingTab />,
              },
              {
                path: 'fingerprints',
                element: <FingerprintsTab />,
              },
              {
                path: 'housing',
                element: <HousingTab />,
              },
            ],
          },
          {
            path: 'inmate/:inmateId',
            element: <BookingLoader />,
            children: [
              {
                path: 'identity',
                element: <IdentityTab />,
              },
              {
                path: 'housing',
                element: <InmateHousingTab />,
              },
              {
                path: 'notes',
                element: <InmateNotesDataGrid />,
              },
              {
                path: 'booking-history',
                element: <BookingHistoryTab />,
              },
            ],
          },
          {
            path: 'inmate/:inmateId',
            element: <BookingLoader />,
            children: [
              {
                path: 'identity',
                element: <IdentityTab />,
              },
              {
                path: 'notes',
                element: <InmateNotesDataGrid />,
              },
            ],
          },
        ],
      },
      {
        path: 'tools/wanted-person',
        children: [
          {
            index: true,
            element: (
              <AuthGuard>
                <WantedPersonList />
              </AuthGuard>
            ),
            handle: {
              allowedRoles: [
                Role.WANTED_PERSON_EDIT,
                Role.WANTED_PERSON_VIEW,
                Role.WANTED_PERSON_VIEW_ADD_ATTEMPT,
              ],
            },
          },
          {
            path: 'new',
            element: <WantedPersonAddEditPage />,
          },
          {
            path: 'edit/:wantedPersonId',
            element: <WantedPersonAddEditPage />,
          },
          {
            path: 'view/:wantedPersonId',
            element: <WantedPersonViewPage />,
          },
        ],
      },
      {
        path: 'jms/operations',
        children: [
          {
            path: 'inmates-movement',
            element: (
              <AppErrorBoundary>
                <InmatesMovementLoader />
              </AppErrorBoundary>
            ),
          },
        ],
      },
      {
        path: 'jms/operations',
        children: [
          {
            path: 'inmates-movement',
            element: <InmatesMovementLoader />,
          },
        ],
      },
      {
        path: 'tools',
        element: <ToolsManagement />,
      },
      {
        path: 'arrests',
        element: <ArrestsToolDataGrid />,
      },
      {
        path: 'tools/permit',
        children: [
          {
            index: true,
            element: (
              <DemoGuard>
                <PermitList />
              </DemoGuard>
            ),
          },
        ],
      },
      {
        path: 'tools/complaints',
        element: (
          <DemoGuard>
            <ComplaintsList />
          </DemoGuard>
        ),
      },
      {
        path: 'tools/law-library',
        element: (
          <AuthGuard>
            <LawLibraryList />
          </AuthGuard>
        ),
      },
      {
        path: 'tools/sex-offenders',
        element: (
          <AuthGuard>
            <SexOffendersList />
          </AuthGuard>
        ),
      },
      {
        path: 'tools/sex-offenders/:id',
        element: (
          <AuthGuard>
            <SexOffendersList />
          </AuthGuard>
        ),
      },
      {
        path: 'tools/bolo',
        element: (
          <DemoGuard>
            <BOLOsList />
          </DemoGuard>
        ),
      },
      {
        path: '/tools/towing-management/towing-company/:id',
        element: (
          <DemoGuard>
            <TowingCompanyDetailsPage />
          </DemoGuard>
        ),
      },
      {
        path: 'tools/towing-management',
        element: (
          <DemoGuard>
            <TowingManagement />
          </DemoGuard>
        ),
      },
      {
        path: 'tools/internal-affairs',
        element: (
          <InternalAffairsGuard>
            <InternalAffairsList />
          </InternalAffairsGuard>
        ),
      },
      {
        path: 'tools/internal-affairs/new',
        element: (
          <InternalAffairsGuard>
            <InternalAffairsAddEditPage />
          </InternalAffairsGuard>
        ),
        children: [
          { path: 'overview', element: <InternalAffairsOverviewForm /> },
          { path: 'people-involved', element: <InternalAffairsPeopleList /> },
          {
            path: 'related-records',
            element: <InternalAffairsRelatedRecords />,
          },
        ],
      },
      {
        path: 'tools/internal-affairs/edit/:InternalAffairsId',
        element: (
          <InternalAffairsGuard>
            <InternalAffairsAddEditPage />
          </InternalAffairsGuard>
        ),
        children: [
          { path: 'overview', element: <InternalAffairsOverviewForm /> },
          { path: 'people-involved', element: <InternalAffairsPeopleList /> },
          {
            path: 'related-records',
            element: <InternalAffairsRelatedRecords />,
          },
          { path: 'audit-trail', element: <InternalAffairsAuditTrail /> },
        ],
      },
      {
        path: 'tools/internal-affairs/view/:ViewInternalAffairsId',
        element: (
          <InternalAffairsGuard>
            <InternalAffairsViewPage />
          </InternalAffairsGuard>
        ),
        children: [
          { path: 'overview', element: <InternalAffairsView /> },
          { path: 'people-involved', element: <InternalAffairsPeopleList /> },
          {
            path: 'related-records',
            element: <InternalAffairsRelatedRecords />,
          },
        ],
      },
      {
        path: 'tools/domestic-incident-reports',
        element: <DomesticIncidentReportList />,
      },
      {
        path: '/tools/towing-management/tow-record/:id',
        element: (
          <DemoGuard>
            <TowRecordDetailsPage />
          </DemoGuard>
        ),
      },
      {
        path: 'tools/cameras',
        children: [
          {
            index: true,
            element: (
              <DemoGuard>
                <CameraList />
              </DemoGuard>
            ),
          },

          {
            path: `/${MainRoutes.CamerasList}/:cameraId/view`,
            element: (
              <DemoGuard>
                <CameraView />
              </DemoGuard>
            ),
          },
          {
            path: `/${MainRoutes.CamerasList}/add`,
            element: (
              <DemoGuard>
                <CameraEdit />
              </DemoGuard>
            ),
          },
          {
            path: `/${MainRoutes.CamerasList}/:cameraId`,
            element: (
              <DemoGuard>
                <CameraEdit />
              </DemoGuard>
            ),
          },
        ],
      },
      {
        path: 'tools/calendar-scheduler',
        element: (
          <AuthGuard>
            <CalendarScheduler />
          </AuthGuard>
        ),
      },
      {
        path: `${MainRoutes.CalendarScheduler}/:eventId`,
        element: (
          <AuthGuard>
            <CalendarScheduler />
          </AuthGuard>
        ),
      },
      {
        path: 'tools/vehicle-maintenance',
        children: [
          {
            index: true,
            element: (
              <AuthGuard>
                <VehicleMaintenanceList />
              </AuthGuard>
            ),
          },
          {
            path: 'view/:vehicleId',
            element: (
              <AuthGuard>
                <VehicleMaintenanceViewPage />
              </AuthGuard>
            ),
          },
        ],
      },
      {
        path: 'tools/building-inspection',
        element: <BuildingLayout />,
        children: [
          {
            index: true,
            element: (
              <AuthGuard>
                <BuildingRecord />
              </AuthGuard>
            ),
          },
          {
            path: ':buildingId',
            children: [
              {
                index: true,
                element: (
                  <AuthGuard>
                    <BuildingInspection />
                  </AuthGuard>
                ),
              },
              {
                path: 'view_inspection/:inspectionId',
                element: (
                  <AuthGuard>
                    <ViewInspection />
                  </AuthGuard>
                ),
              },
              {
                path: 'start_inspection/:inspectionId',
                element: (
                  <AuthGuard>
                    <StartInspection />
                  </AuthGuard>
                ),
              },
            ],
          },
        ],
      },
      {
        path: MainRoutes.Settings,
        element: <SettingsPage />,
      },
      {
        path: 'keyboard-shortcuts',
        element: <KeyboardShortcuts />,
      },
    ],
  },
  // Fullscreen pages section
  {
    path: '/', // Base path for this layout group
    element: (
      <AuthGuard>
        <MainLayout isFullScreen={true} />
      </AuthGuard>
    ),
    children: [
      {
        path: 'my-cfs',
        element: <CFSFirstRespondersPage />,
      },
      {
        path: 'map-view',
        element: <FirstResponderDashboardPage />,
      },
      {
        path: 'tools/location-markers',
        element: <LocationMarkers />,
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundScreen />,
  },
  {
    path: 'forbidden',
    element: <ForbiddenScreen />,
  },
];

export const AppRoutes = () => {
  return (
    <GeolocationProvider>
      {/* <RouteChangeTracker /> */}
      <RouterProvider router={createBrowserRouter(routes)} />
    </GeolocationProvider>
  );
};
